import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { RouteComponentProps } from 'react-router';
import SearchPageComponent from '../../components/Search';
import {
  STORE_ADVERT,
  STORE_VIDEO,
  AdvertSlot,
  STORE_TAG,
  STORE_ROUTER
} from '../../constants';
import { SearchQuery, Tag } from '../../models';
import { AdvertStore, TagStore, VideoStore, RouterStore } from '../../stores';
import { parseQueryString } from '../../utils/querystring';

export interface TagFilterGroup {
  tags: Array<Tag>;
  title: string;
}

// tslint:disable-next-line no-any
export interface SearchPageProps extends RouteComponentProps<any> {}

export interface SearchPageState {
  loading: boolean;
  query: SearchQuery;
  searchableTags: Array<Tag>;
  tagFilterGroups: Array<TagFilterGroup>;
}

@inject(STORE_ADVERT)
@inject(STORE_TAG)
@inject(STORE_VIDEO)
@inject(STORE_ROUTER)
@observer
export class SearchPage extends React.Component<
  SearchPageProps,
  SearchPageState
> {
  private _isMounted: boolean = false;

  constructor(props: SearchPageProps) {
    super(props);

    this.state = {
      loading: false,
      query: new SearchQuery(),
      searchableTags: [],
      tagFilterGroups: []
    };
  }

  async componentDidMount() {
    this._isMounted = true;
    await this.loadSearchResults();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  async componentDidUpdate(previousProps: SearchPageProps) {
    const {
      location: { search: oldSearch }
    } = previousProps;
    const {
      location: { search }
    } = this.props;

    if (search !== oldSearch) {
      await this.loadSearchResults();
    }
  }

  render() {
    const tagStore = this.props[STORE_TAG] as TagStore;
    const videoStore = this.props[STORE_VIDEO] as VideoStore;
    const advertStore = this.props[STORE_ADVERT] as AdvertStore;
    const { state } = this;

    const loading =
      state.loading ||
      videoStore.searchVideosLoading.running ||
      tagStore.fetchingTags.running;

    const error =
      !loading &&
      !!(videoStore.searchVideosLoading.error || tagStore.fetchingTags.error);

    const { location, match } = this.props;
    const { query, searchableTags, tagFilterGroups } = this.state;

    return (
      <SearchPageComponent
        advertSlotOne={advertStore.adverts[AdvertSlot.CategoryIndexOne]}
        advertSlotTwo={advertStore.adverts[AdvertSlot.CategoryIndexTwo]}
        error={error}
        loading={loading}
        location={location}
        pageUrl={match.url + location.search}
        searchableTags={searchableTags}
        searchResult={videoStore.searchResults}
        onAddSearchTag={this.addTagToSearchQuery}
        onRemoveSearchTag={this.removeTagFromSearchQuery}
        tagFilterGroups={tagFilterGroups}
        query={query}
      />
    );
  }

  private loadSearchResults = async () => {
    const advertStore = this.props[STORE_ADVERT] as AdvertStore;
    const tagStore = this.props[STORE_TAG] as TagStore;
    const videoStore = this.props[STORE_VIDEO] as VideoStore;

    this.setState({
      loading: true
    });

    try {
      await tagStore.fetchTags();

      if (!this._isMounted) {
        return;
      }

      const tagFilterGroups = this.getTagFilterGroups();
      const searchableTags = this.getSearchableTags(tagFilterGroups);

      this.setState({
        searchableTags,
        tagFilterGroups
      });

      const query = this.setQueryFromParams();

      await advertStore.fetchAdvertsForSlot(AdvertSlot.CategoryIndexOne);
      await advertStore.fetchAdvertsForSlot(AdvertSlot.CategoryIndexTwo);
      await videoStore.fetchVideosForSearch(query);
    } finally {
      if (!this._isMounted) {
        return;
      }

      this.setState({
        loading: false
      });
    }
  };

  private setQueryFromParams = (): SearchQuery => {
    const { query } = this.state;
    const { tagNamesString } = this.getRouteParams();

    const tagNames: Array<string> = tagNamesString
      ? Tag.deserializeTagsString(tagNamesString)
      : [];

    // Try to find tag by name, use a dummy "unknown" tag if the search
    // page is loaded for a tag we can't find by name
    query.tags = tagNames.length ? this.getTagsFromTagNames(tagNames) : [];

    this.setState({
      query
    });

    return query;
  };

  private getTagsFromTagNames = (tagNames: Array<String>): Array<Tag> => {
    const tagStore = this.props[STORE_TAG] as TagStore;
    let addedUnknownTag = false;

    return tagNames.reduce((tags: Array<Tag>, tagName: string): Array<Tag> => {
      const tag = tagStore.getTagByName(tagName);

      if (tag) {
        tags.push(tag);
      } else if (!addedUnknownTag) {
        addedUnknownTag = true;
        tags.push(new Tag(-1, tagName, `#DDDDDD`));
      }

      return tags;
    }, []);
  };

  private addTagToSearchQuery = (tag: Tag) => {
    const { query } = this.state;

    this.redirectToQueryResults([...query.tags, tag]);
  };

  private removeTagFromSearchQuery = (tag: Tag) => {
    const { query } = this.state;

    this.redirectToQueryResults(
      query.tags.filter(queryTag => queryTag.id !== tag.id)
    );
  };

  private redirectToQueryResults = (tags: Array<Tag>) => {
    const routerStore = this.props[STORE_ROUTER] as RouterStore;

    routerStore.history.push(`/search?tags=${Tag.serializeTagsString(tags)}`);
  };

  private getRouteParams = (
    props: SearchPageProps = this.props
  ): { tagNamesString?: string } => {
    const {
      location: { search }
    } = props;

    const params = parseQueryString(search);

    const tagNamesString = params.tags ? params.tags.toString() : undefined;

    return {
      tagNamesString
    };
  };

  private isTag(maybeTag?: Tag): maybeTag is Tag {
    return maybeTag !== undefined;
  }

  private getTagFilterGroups = (): Array<TagFilterGroup> => {
    const tagStore = this.props[STORE_TAG] as TagStore;

    if (!tagStore) {
      return [];
    }

    return [
      {
        tags: ['Beginner', 'Intermediate', 'Advanced']
          .map(tagStore.getTagByName, tagStore)
          .filter(this.isTag),
        title: 'Difficulty'
      },
      {
        tags: ['Age 6+', 'Age 12+', 'Age 16+']
          .map(tagStore.getTagByName, tagStore)
          .filter(this.isTag),
        title: 'Age Range'
      }
    ];
  };

  private getSearchableTags = (
    tagFilterGroups: Array<TagFilterGroup>
  ): Array<Tag> => {
    const tagStore = this.props[STORE_TAG] as TagStore;

    if (!tagStore) {
      return [];
    }

    return tagStore.tags.filter(tag =>
      tagFilterGroups.every(tagFilterGroup =>
        tagFilterGroup.tags.every(filterTag => filterTag.id !== tag.id)
      )
    );
  };
}

export default SearchPage;



// WEBPACK FOOTER //
// ./src/app/containers/SearchPage/index.tsx